export type ResumeNext = {
  kind: string;
  quote_id: string | null;
  case_id: string | null;
  event_id: string | null;
};

export type ConfirmResponse = {
  status: "confirmed" | "incomplete" | "not_pending";
  next: ResumeNext;
};

const SUBMISSION_ID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The submission the sign-in was for, from `?s=`. Anything that is not a
 * UUID is treated as absent, so it never reaches a request path. */
export function submissionIdFromSearch(search: string): string | null {
  const raw = new URLSearchParams(search).get("s");
  return raw !== null && SUBMISSION_ID.test(raw) ? raw : null;
}

/** Whether the sign-in came through the confirmation reminder's link, which
 * marks its destination with `?via=reminder`. */
export function isReminderLink(search: string): boolean {
  return new URLSearchParams(search).get("via") === "reminder";
}

function withEvent(path: string, eventId: string | null): string {
  if (!eventId) return path;
  return `${path}?${new URLSearchParams({ event_id: eventId }).toString()}`;
}

/** Where a confirmed submission lands: straight on the quote or case it
 * produced, skipping account Home. A lead with no quote to open goes back
 * to the page it left, when `returnPath` names one, else to the other-options
 * page. Anything unreachable, including a kind or id this version does not
 * know, routes to Home. */
export function resumeDestination(
  next: ResumeNext,
  returnPath: string | null = null,
): string {
  if (next.kind === "quote" && next.quote_id) {
    return withEvent(
      `/quotes/${encodeURIComponent(next.quote_id)}`,
      next.event_id,
    );
  }
  if (next.kind === "case" && next.case_id) {
    return withEvent(
      `/cases/${encodeURIComponent(next.case_id)}`,
      next.event_id,
    );
  }
  if (next.kind === "other_options") {
    return returnPath ?? withEvent("/start/other-options", next.event_id);
  }
  return "/";
}

/** The bounded outcome recorded for analytics. */
export type ResumeOutcome = "quote" | "case" | "other_options" | "home";

export function resumeOutcome(next: ResumeNext): ResumeOutcome {
  if (next.kind === "other_options") return "other_options";
  const destination = resumeDestination(next);
  if (destination.startsWith("/quotes/")) return "quote";
  if (destination.startsWith("/cases/")) return "case";
  return "home";
}

export type ResumeFailure = "not_confirmed" | "error";

/** A confirm that left a record unfinished keeps the submission pending, so it
 * reads as a failure that trying again can fix, not as a landing. */
export function confirmFailure(
  response: ConfirmResponse,
): ResumeFailure | null {
  return response.status === "incomplete" ? "error" : null;
}

const REFUSED = new Set([403, 404, 409]);

/** A refused confirm (unknown submission, another person's, no proof, or
 * expired) and a server fault read differently: the first cannot be
 * fixed by retrying. */
export function resumeFailure(statusCode: number | undefined): ResumeFailure {
  return statusCode !== undefined && REFUSED.has(statusCode)
    ? "not_confirmed"
    : "error";
}
